import { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/auth/PasswordInput";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

export default function LoginPage() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
    } catch (e2) {
      setError(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="hidden lg:flex flex-col justify-between bg-[hsl(var(--sidebar))] kgd-grain text-white p-12 relative overflow-hidden">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-primary/25 blur-3xl" />
        <div className="absolute -left-10 bottom-10 h-64 w-64 rounded-full bg-[hsl(var(--gold))]/15 blur-3xl" />
        <div className="relative text-[11px] uppercase tracking-[0.3em] text-white/50">Plateforme de gestion</div>
        <div className="relative">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
            Réseau, TPE<br />et trésorerie,<br /><span className="text-primary">un seul pilotage.</span>
          </h1>
          <p className="mt-6 text-white/60 max-w-md text-sm leading-relaxed">
            Loto, paris sportifs et hippiques : remontées journalières, gains, commissions, dépenses et stocks, tracés de bout en bout.
          </p>
        </div>
        <div className="relative text-xs text-white/40">© KORIS GAMING DISTRIBUTION</div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 fade-up" data-testid="login-form">
          <img src="/logo-kgd.jpeg" alt="KORIS Gaming Distribution" className="h-24 object-contain -ml-2" data-testid="login-logo" />
          <div>
            <h2 className="text-2xl font-extrabold tracking-tight">Connexion</h2>
            <p className="text-sm text-muted-foreground mt-1">Accès réservé aux comptes créés par l'administrateur.</p>
          </div>
          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 h-11" data-testid="login-email-input" />
            </div>
            <div>
              <div className="flex items-center justify-between"><Label htmlFor="password">Mot de passe</Label><Link to="/mot-de-passe-oublie" className="text-xs text-muted-foreground hover:text-primary" data-testid="forgot-password-link">Mot de passe oublié ?</Link></div>
              <div className="mt-1"><PasswordInput id="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-11" testId="login-password-input" /></div>
            </div>
          </div>
          {error && <div className="text-sm text-primary bg-red-50 border border-red-200 rounded p-2.5" data-testid="login-error">{error}</div>}
          <Button type="submit" className="w-full h-11 font-semibold" disabled={busy} data-testid="login-submit-button">
            <Lock className="h-4 w-4 mr-2" />{busy ? "Connexion…" : "Se connecter"}
          </Button>
        </form>
      </div>
    </div>
  );
}
